import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
interface BentoCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  colors: string[];
  delay: number;
}
export const BentoCard: React.FC<BentoCardProps> = ({
  title,
  value,
  subtitle,
  colors,
  delay
}) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, {
    once: true,
    amount: 0.3
  });
  const [count, setCount] = useState(0);
  // Parse end value once
  const endValue = typeof value === 'string' ? parseInt(value.replace(/[^0-9]/g, '')) : value;
  // Format value for display
  const formattedValue = typeof value === 'string' ? value.startsWith('$') ? `$${count.toLocaleString()}${value.includes('M') ? 'M' : ''}${value.includes('+') ? '+' : ''}${value.includes('%') ? '%' : ''}` : `${count.toLocaleString()}${value.includes('+') ? '+' : ''}${value.includes('%') ? '%' : ''}` : count.toLocaleString();
  // Handle counting animation
  useEffect(() => {
    if (!isInView) return;
    // Delay start based on card's delay prop
    const startTimer = setTimeout(() => {
      let startTime: number | null = null;
      const duration = 1500;
      const step = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        if (elapsed >= duration) {
          setCount(endValue);
          return;
        }
        // Use easeOutExpo for smoother animation
        const progress = 1 - Math.pow(2, -10 * elapsed / duration);
        setCount(Math.floor(progress * endValue));
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }, delay * 500);
    return () => clearTimeout(startTimer);
  }, [isInView, endValue, delay]);
  return <motion.div ref={cardRef} initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: delay * 0.2
  }} className="relative overflow-hidden h-full rounded-lg shadow-lg bg-[#0a1445]" data-id="element-27">
      {/* Blue gradient overlay */}
      <div className="absolute inset-0" style={{
      background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1] || colors[0]} 100%)`,
      opacity: 0.3,
      borderRadius: '0.5rem'
    }} data-id="element-28" />
      {/* Blue bottom border accent */}
      <div className="absolute bottom-0 left-0 right-0 h-1" style={{
      background: `linear-gradient(90deg, ${colors[0]} 0%, ${colors[1] || colors[0]} 100%)`,
      opacity: 0.9
    }} data-id="element-29" />
      <div className="relative z-10 p-6 md:p-8" data-id="element-30">
        <h3 className="text-sm sm:text-base md:text-lg text-white/80 font-medium" data-id="element-31">
          {title}
        </h3>
        <p className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 text-white" data-id="element-32">
          {formattedValue}
        </p>
        {subtitle && <p className="text-sm text-white/70" data-id="element-33">{subtitle}</p>}
      </div>
    </motion.div>;
};